/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, useEffect, useState} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {Crumb} from '@ion/core/src/types/entities';
import {GlobalGroupProps} from '@ion/core/src/types/general';
import {BoundaryMode} from '../../types/types';
import {useDrawingComplete} from '../hooks/useDrawingComplete';
import {CreateRadial} from './CreateRadial';
import {GeofenceSearch} from './GeofenceSearch';
import SaveSettings from './SaveSettings';

type RadialGeoFenceProps = GlobalGroupProps & {
  completeDrawing: Function;
  map: __esri.Map;
  view: __esri.MapView;
  isVisible: boolean;
  assetCategories?: string[];
  geofenceSave?: Function;
  selectCrumbs?: Function;
  crumbs?: Crumb[];
  savingGeofence?: boolean;
  mapViewEl?: HTMLElement;
  geoFencesGraphicsLayerRef?: {
    current: null | __esri.Layer;
  };
  sketchViewModelRef?: {
    current: null | __esri.Layer;
  };
  selectedMapPointRef?: {
    current: null | __esri.Layer;
  };
  cancelDrawing?: Function;
  mapClickHandler?: any;
  setMapClickHandler?: (val: any) => void;
  handleBack?: Function;
  closeGeoFencePanel?: Function;
  handleApplyBoundary?: Function;
  isBoundary?: boolean;
  boundaryMode?: BoundaryMode;
  useHereApi?: boolean;
  isPhone?: boolean;
  isFMTracking?: boolean;
  toggleShowBoundaryPanel?: Function;
  onSearchTimeChange?: Function;
};

export function RadialGeoFence({
  map,
  view,
  boundaryMode,
  completeDrawing,
  isVisible,
  assetCategories,
  geofenceSave,
  selectCrumbs,
  crumbs,
  savingGeofence,
  mapViewEl,
  geoFencesGraphicsLayerRef,
  sketchViewModelRef,
  selectedMapPointRef,
  cancelDrawing,
  mapClickHandler,
  setMapClickHandler,
  handleBack,
  closeGeoFencePanel,
  isBoundary,
  accountMap,
  globalGroup,
  handleApplyBoundary,
  isPhone,
  onSearchTimeChange,
  toggleShowBoundaryPanel,
  useHereApi = false,
  isFMTracking = false
}: RadialGeoFenceProps) {
  const [radiusValue, setRadiusValue]                 = useState(boundaryMode === BoundaryMode.Vicinity ? 1 : 100);
  const [radiusFromMap, setRadiusFromMap]             = useState(null);
  const [radiusFromPanel, setRadiusFromPanel]         = useState(null);
  const [drawingComplete, setDrawingComplete]         = useState(false);
  const [finishedDrawing, onCompleteDrawing]          = useDrawingComplete(isBoundary, handleApplyBoundary);
  const [finalDrawingDetails, setFinalDrawingDetails] = useState(null);
  const [selectedMapPoint, setSelectedMapPoint]       = useState(selectedMapPointRef);

  useEffect(() => {
    if (finalDrawingDetails) completeDrawing(finalDrawingDetails);
  }, [finalDrawingDetails]);

  useEffect(() => {
    setSelectedMapPoint(selectedMapPointRef);
  }, [selectedMapPointRef?.current]);

  function handleRadiusFromPanel(value) {
    setRadiusFromMap(null);
    setRadiusFromPanel(value);
    setRadiusValue(value);
  }

  function handleRadiusFromMap(value) {
    setRadiusFromPanel(null);
    setRadiusFromMap(value);
    setRadiusValue(value);
  }

  function handleRadiusInput(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    handleRadiusFromPanel(parseInt(e.target.value) || 0);
  }

  const handleSetSelectedMapPoint = function(value) {
    setSelectedMapPoint(value);
    // update the radius value to user to notice.
    handleRadiusFromPanel(boundaryMode === BoundaryMode.Vicinity ? 1 : 1000);
  };

  const radius = radiusFromMap || radiusValue;

  return (
    <>
      {finishedDrawing ? (
        <SaveSettings assetCategories={assetCategories}
          accountMap={accountMap}
          globalGroup={globalGroup}
          finalDrawingDetails={finalDrawingDetails}
          geofenceSave={geofenceSave}
          selectCrumbs={selectCrumbs}
          closeGeoFencePanel={closeGeoFencePanel}
          handleBack={handleBack}
          crumbs={crumbs}
          savingGeofence={savingGeofence}
          geoFencesGraphicsLayerRef={geoFencesGraphicsLayerRef}
          sketchViewModelRef={sketchViewModelRef}
          selectedMapPointRef={selectedMapPoint}
          map={map}
          mapClickHandler={mapClickHandler} />
      ) : (
        <GeofenceSearch view={view}
          map={map}
          useHereApi={useHereApi}
          isVisible={isVisible}
          radius={radius}
          boundaryMode={boundaryMode}
          handleRadiusInput={handleRadiusInput}
          drawingComplete={drawingComplete}
          toggleShowBoundaryPanel={toggleShowBoundaryPanel}
          handleBack={handleBack}
          handleRadiusFromPanel={handleRadiusFromPanel}
          crumbs={crumbs}
          selectCrumbs={selectCrumbs}
          geofenceType={translate('maps:geofences.radial')}
          handleCompleteDrawing={onCompleteDrawing}
          onSearchTimeChange={onSearchTimeChange}
          handleSetSelectedMapPoint={handleSetSelectedMapPoint}
          mapClickHandler={mapClickHandler}
          isPhone={isPhone} />
      )}
      <CreateRadial map={map}
        mapView={view}
        boundaryMode={isBoundary ? boundaryMode : null}
        isFMTracking={isFMTracking}
        radius={radiusValue}
        setRadiusFromMap={handleRadiusFromMap}
        radiusFromPanel={radiusFromPanel}
        setDrawingComplete={setDrawingComplete}
        finalDrawingDetails={finalDrawingDetails}
        setFinalDrawingDetails={setFinalDrawingDetails}
        finishedDrawing={finishedDrawing}
        mapViewEl={mapViewEl}
        geoFencesGraphicsLayerRef={geoFencesGraphicsLayerRef}
        sketchViewModelRef={sketchViewModelRef}
        selectedMapPointRef={selectedMapPoint}
        cancelDrawing={cancelDrawing}
        mapClickHandler={mapClickHandler}
        setMapClickHandler={setMapClickHandler}
        isPhone={isPhone} />
    </>
  );
}
